/**
 * EmptyInboxModal
 *
 * Modal exibido na PRIMEIRA vez que o usuário com canal WhatsApp Business
 * abre o inbox e NÃO tem nenhum contato/conversa importado. Avisa que o
 * Business não traz contatos automaticamente e oferece duas ações:
 *
 *   - Recarregar (azul, secundário): tenta recarregar a página. Após clicar
 *     fica DESATIVADO — força o usuário a abrir o tutorial ao invés de ficar
 *     reclicando inutilmente.
 *   - Ver tutorial (verde, primário): leva pra página de tutorial passo-a-passo
 *     da conexão Business e marca o modal como visto (localStorage). Não aparece
 *     mais para esse usuário/dispositivo.
 *
 * Persistência: localStorage `infinitsales-empty-inbox-warned-v1`. Marcada
 * apenas quando o usuário clica em "Ver tutorial" (intenção explícita).
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void   — chamado ao clicar Ver tutorial
 *   - onSeeTutorial: () => void  — navega para /tutorials#wa-business-connect
 *   - onReload: () => void  — recarrega a página
 */
import { useState } from 'react';
import { AlertCircle, BookOpen, RefreshCw, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const EmptyInboxModal = ({ open, onClose, onSeeTutorial, onReload }) => {
  const { colors } = useTheme();
  const [reloadDisabled, setReloadDisabled] = useState(false);

  if (!open) return null;

  const handleReload = () => {
    if (reloadDisabled) return;
    setReloadDisabled(true);
    onReload?.();
  };

  const handleSeeTutorial = () => {
    onSeeTutorial?.();
    // Modal fecha após navegar — pai marca o localStorage no onClose
    onClose?.();
  };

  const styles = {
    backdrop: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(2px)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      animation: 'emptyInboxFadeIn 0.18s ease-out',
    },
    modal: {
      width: 'min(480px, 100%)',
      backgroundColor: colors.bgPrimary,
      borderRadius: '14px',
      border: `1px solid ${colors.borderColor}`,
      boxShadow: '0 14px 40px rgba(0,0,0,0.22)',
      overflow: 'hidden',
      animation: 'emptyInboxScaleIn 0.2s ease-out',
    },
    header: {
      padding: '20px 22px 8px 22px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    iconBadge: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      backgroundColor: '#f59e0b15',
      color: '#f59e0b',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    title: { fontSize: '17px', fontWeight: 700, color: colors.textPrimary },
    body: {
      padding: '6px 22px 18px 22px',
      fontSize: '14px',
      color: colors.textSecondary,
      lineHeight: 1.55,
    },
    footer: {
      padding: '14px 22px 20px 22px',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
    reloadBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '9px 18px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: reloadDisabled ? colors.bgTertiary : '#3b82f6',
      color: reloadDisabled ? colors.textTertiary : 'white',
      fontSize: '13px',
      fontWeight: 600,
      cursor: reloadDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease',
    },
    tutorialBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '9px 18px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'opacity 0.15s ease',
    },
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.iconBadge}>
            <AlertCircle size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.title}>Nenhum contato foi exportado</div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              color: colors.textTertiary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div style={styles.body}>
          <p style={{ margin: 0 }}>
            Você tem contatos no seu WhatsApp, mas eles não foram trazidos na conexão para o seu inbox.
          </p>
          <p style={{ marginTop: '10px', marginBottom: 0 }}>
            Isso é normal no <strong>WhatsApp Business via Meta Cloud API</strong> — os contatos só aparecem aqui depois que cada um te envia uma mensagem ou você inicia a conversa via template aprovado.
          </p>
          <p style={{ marginTop: '10px', marginBottom: 0, color: colors.textTertiary, fontSize: '13px' }}>
            Veja o tutorial passo a passo para entender como funciona e o que fazer para suas conversas começarem a aparecer.
          </p>
        </div>

        <div style={styles.footer}>
          <button
            style={styles.reloadBtn}
            onClick={handleReload}
            disabled={reloadDisabled}
            onMouseEnter={(e) => {
              if (!reloadDisabled) e.currentTarget.style.opacity = '0.88';
            }}
            onMouseLeave={(e) => {
              if (!reloadDisabled) e.currentTarget.style.opacity = '1';
            }}
            title={reloadDisabled ? 'Já recarregou — agora veja o tutorial' : 'Recarregar a página'}
          >
            <RefreshCw size={14} />
            {reloadDisabled ? 'Recarregado' : 'Recarregar'}
          </button>
          <button
            style={styles.tutorialBtn}
            onClick={handleSeeTutorial}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <BookOpen size={14} />
            Ver tutorial
          </button>
        </div>
      </div>

      <style>{`
        @keyframes emptyInboxFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes emptyInboxScaleIn { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default EmptyInboxModal;
